import { WorkflowItem } from '@cibo/core'
import { PaginatedFieldsContext } from '@cibo/landmanager'
import { Loading, useAuth, useOrgUser } from '@cibo/profile'
import {
  AddWorkflowInformation,
  ProgramIcon,
  ProgramLimitFeedback,
  ProgramLimitProvider,
  SelectFieldsFromWorkflowTable,
  useDeleteWorkflow,
  useProgram,
  useProgramContent,
  useProgramLimit,
  useWorkflow,
} from '@cibo/programs'
import { FriendlyError, ResponsiveDialog, WindowLocation } from '@cibo/ui'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { LayoutSection } from 'components/ImpactLayout'
import { ROUTE } from 'navigation/types'
import { PROGRAM_ROUTES } from 'navigation/types/routes'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, generatePath, useLocation, useNavigate, useParams } from 'react-router-dom'
import { WorkflowActionBar } from './WorkflowActionBar'

export const WorkflowView = ({
  returnTo,
  workflow,
  workflowId,
  isCreating,
  userIsWorkflowOwner,
}: {
  returnTo: string
  workflow: WorkflowItem
  workflowId: string
  isCreating: boolean
  userIsWorkflowOwner: boolean
}) => {
  const { benchmark, fieldCounts, programId, ownedBy } = workflow
  const { data: programContent } = useProgramContent(programId)

  const program = useProgram({ programId })
  const deleteWorkflow = useDeleteWorkflow()
  const { enqueueSnackbar } = useSnackbar()

  const { isReady, display, limits } = useProgramLimit()
  const noSpaceLimit = !limits || !limits[0].display ? false : limits[0].limitType === 'noSpace'
  const [isOpen, setIsOpen] = useState<boolean>(false)
  useEffect(() => {
    if (isReady) {
      if (isCreating) setIsOpen(display)
      if (noSpaceLimit) setIsOpen(display)
    }
  }, [isReady, display, limits, isCreating, noSpaceLimit])
  const { t } = useTranslation('impact/features/Workflows')
  const navigate = useNavigate()
  const [viewingSummary, setViewingSummary] = useState<boolean>(benchmark === 'review')
  const { selectedRowIds } = useContext(PaginatedFieldsContext)

  useEffect(() => {
    !fieldCounts.incomplete && setViewingSummary(true)
  }, [])

  const onCommit = () => {
    navigate(`/${ROUTE.PROGRAMS}/${generatePath(ROUTE.COMMIT, { programId })}`, {
      state: { fields: selectedRowIds, programContent, programId, workflow },
    })
  }

  const onClose = () => {
    setIsOpen(false)
  }

  const onPressDelete = () => {
    if (!workflowId) return
    deleteWorkflow.mutateAsync(workflowId).then(() => {
      onClose()
      navigate(returnTo)
      enqueueSnackbar(t('successMessage', { context: benchmark, workflowName: workflow.name }), {
        variant: 'success',
      })
    })
  }

  return (
    <>
      <LayoutSection container={!!viewingSummary} autoHeight>
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Stack direction="row" alignItems="center" spacing={1}>
            {!!program.data && <ProgramIcon program={program.data} />}
            <Stack>
              <Typography variant="h6">
                {viewingSummary
                  ? fieldCounts.pass > 0
                    ? t('eligibleFields', {
                        count: fieldCounts.pass,
                        programName: programContent?.content.fields.title,
                      })
                    : t('noEligibleFields', {
                        programName: programContent?.content.fields.title,
                      })
                  : t('fillInDetails', {
                      context: benchmark,
                      programName: programContent?.content.fields.title,
                    })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Divider />

        {viewingSummary ? (
          <>
            <ProgramLimitFeedback
              create={isCreating}
              growerName={
                ownedBy.givenName
                  ? `${ownedBy.givenName} ${ownedBy.familyName}`
                  : ownedBy.userPrimaryEmail
              }
              additionalMdContent={{ overCommit: t('limitInfo', { benchmark }) }}
            />
            <SelectFieldsFromWorkflowTable
              programId={programId}
              workflowId={workflowId}
              benchmark={benchmark}
              // @ts-ignore mui has not appropriately typed `componentProps` but it does exist
              componentProps={{
                components: {
                  Toolbar: () => <Box width="20px" />,
                },
              }}
              ReasonDialogActions={({ onCloseDialog }) => (
                <Button
                  variant="contained"
                  onClick={() => {
                    onCloseDialog()
                    setViewingSummary(false)
                  }}
                >
                  {t('reviewInformation')}
                </Button>
              )}
            />
          </>
        ) : (
          <AddWorkflowInformation
            workflowId={workflowId}
            onContinue={() => setViewingSummary(true)}
          />
        )}
      </LayoutSection>

      <WorkflowActionBar
        workflow={workflow}
        viewingSummary={viewingSummary}
        setViewingSummary={setViewingSummary}
        returnTo={returnTo}
        onCommit={onCommit}
        userIsWorkflowOwner={userIsWorkflowOwner}
      />
      <ResponsiveDialog open={!viewingSummary && isOpen} onClose={onClose}>
        <DialogContent>
          <ProgramLimitFeedback
            create={isCreating}
            growerName={
              ownedBy.givenName
                ? `${ownedBy.givenName} ${ownedBy.familyName}`
                : ownedBy.userPrimaryEmail
            }
          />
        </DialogContent>
        <DialogActions>
          {noSpaceLimit ? (
            <>
              <Button onClick={onClose} variant="outlined">
                {t('cancel')}
              </Button>
              <Button onClick={onPressDelete} variant="contained">
                {t('remove')}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} variant="contained" data-testid="warning-modal-ok">
              {t('ok')}
            </Button>
          )}
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}

export const Workflow = () => {
  const { state } = useLocation() as WindowLocation<{ isCreating?: boolean }>
  const { workflowId } = useParams()
  const { isLoading, isError, data: workflow } = useWorkflow(workflowId)
  const { t } = useTranslation('impact/features/Workflows')
  const { userId } = useAuth()
  const { data: orgUser } = useOrgUser(workflow?.ownedBy.userId)
  const returnTo = generatePath(PROGRAM_ROUTES.DETAIL, { programId: workflow?.programId || '' })

  if (!workflowId) {
    return <Navigate to="/programs" />
  }
  if (isLoading) {
    return <Loading large />
  }
  if (isError) {
    return <FriendlyError message={t('workflowError')} />
  }
  if (!workflow) return null

  return (
    <ProgramLimitProvider
      orgId={orgUser?.user.org?.id}
      workflowId={workflowId}
      userId={workflow.ownedBy.userId}
      programId={workflow.programId}
    >
      <WorkflowView
        workflow={workflow}
        workflowId={workflowId}
        returnTo={returnTo}
        isCreating={!!state?.isCreating}
        userIsWorkflowOwner={workflow.ownedBy.userId === userId}
      />
    </ProgramLimitProvider>
  )
}
